import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import VouchWall from "@/components/VouchWall";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Coins,
  Crown,
  Fire,
  Hash,
  IdentificationBadge,
  Lightning,
  Lock,
  Skull,
  Truck,
  UserCircle,
  Warning,
  Wrench,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import GP from "@/components/GP";
import ProductCard from "@/components/ProductCard";
import NameStrike from "@/components/NameStrike";
import NameWall from "@/components/NameWall";
import RsnChecker from "@/components/RsnChecker";
import Counter from "@/components/Counter";

const ICONS = {
  crown: Crown,
  lightning: Lightning,
  coins: Coins,
  hash: Hash,
  "user-circle": UserCircle,
  skull: Skull,
  wrench: Wrench,
  "identification-badge": IdentificationBadge,
};

const QUEST_STEPS = [
  {
    icon: Crown,
    label: "Step 01",
    title: "Choose your drop",
    copy: "Search the live vault by tier, price, or rarity. Every listing is available right now.",
  },
  {
    icon: Lock,
    label: "Step 02",
    title: "Lock the trade",
    copy: "Checkout reserves the listing and removes it from public inventory before anyone can snipe it.",
  },
  {
    icon: Truck,
    label: "Step 03",
    title: "Claim the name",
    copy: "Track the order from your account and receive the secured delivery details when payment clears.",
  },
];

function RuneTicker({ items }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  const row = [...list.slice(0, 18), ...list.slice(0, 18)];
  return (
    <div className="rune-ticker" data-testid="marquee">
      <div className="animate-marquee flex w-max items-center">
        {row.map((product, index) => (
          <div key={`${product.id}-${index}`} className="flex shrink-0 items-center gap-3 px-7">
            <span className="h-1.5 w-1.5 rotate-45 bg-[#ff981f]" />
            <span className="font-rs text-xl font-bold text-[#f5e7c5]">{product.title}</span>
            <GP m={product.price} obo={product.obo} coin size={17} className="text-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

function VaultSlot({ product, index }) {
  if (!product) {
    return <div className="vault-slot opacity-30" aria-hidden="true" />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.15 + index * 0.06 }}
    >
      <Link
        to={`/shop?q=${encodeURIComponent(product.title)}`}
        className="vault-slot group flex min-h-[112px] flex-col justify-between p-3"
        data-testid={`vault-slot-${index}`}
      >
        <div className="flex items-start justify-between gap-2">
          <Crown size={18} weight={product.rarity === "mythic" ? "fill" : "duotone"} className="text-[#ff981f]" />
          <span className="font-rs text-[9px] uppercase tracking-[0.12em] text-[#98876c]">{product.rarity}</span>
        </div>
        <div>
          <div className="truncate font-rs text-[15px] font-bold text-[#ffe83a] rs-shadow">{product.title}</div>
          <GP m={product.price} obo={product.obo} coin size={14} className="mt-1 text-sm" />
        </div>
      </Link>
    </motion.div>
  );
}

function MarketStat({ label, value, suffix = "" }) {
  return (
    <div className="text-center">
      <div className="font-rs text-3xl font-bold text-[#ffe83a] rs-shadow md:text-4xl">
        <Counter to={value} suffix={suffix} />
      </div>
      <div className="mt-1 font-rs text-[10px] uppercase tracking-[0.16em] text-[#c7a86b]">{label}</div>
    </div>
  );
}

function MarketBoard({ categories }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {categories.map((category, index) => {
        const Icon = ICONS[category.icon] || Crown;
        return (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: index * 0.06 }}
          >
            <Link
              to={`/shop?cat=${category.slug}`}
              className="market-plaque group block h-full p-5"
              data-testid={`category-tile-${category.slug}`}
            >
              <div className="mb-9 flex items-start justify-between">
                <span className="grid h-11 w-11 place-items-center border border-[#796542] bg-black/25 text-[#ff981f] shadow-inner">
                  <Icon size={25} weight="duotone" />
                </span>
                <span className="font-rs text-[10px] uppercase tracking-[0.18em] text-[#9a8664]">Vault 0{index + 1}</span>
              </div>
              <div className="font-display text-3xl text-[#f4ead3] transition-colors group-hover:text-[#ffe83a]">{category.name}</div>
              <p className="mt-2 min-h-10 text-sm text-[#aa9d87]">{category.tagline}</p>
              <div className="mt-5 flex items-end justify-between border-t border-[#5e4e35] pt-4">
                <div>
                  <div className="font-rs text-2xl font-bold text-[#ffe83a] rs-shadow">{category.count}</div>
                  <div className="font-rs text-[9px] uppercase tracking-[0.14em] text-[#9a8664]">Available</div>
                </div>
                <ArrowRight size={22} className="text-[#ff981f] transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function HomeReforged() {
  const { settings: layoutSettings } = useOutletContext();
  const { data, isLoading } = useQuery({
    queryKey: ["storefront"],
    queryFn: () => api.get("/storefront").then((response) => response.data),
    staleTime: 60_000,
  });

  const settings = data?.settings || layoutSettings || {};
  const stats = data?.stats || {};
  const categories = data?.categories || [];
  const featured = data?.featured || [];
  const market = data?.market || [];
  const slots = Array.from({ length: 8 }, (_, index) => featured[index]);

  return (
    <div className="bg-[#070604]" data-testid="home-page">
      <section className="osrs-hero relative flex min-h-[760px] items-center overflow-hidden border-b border-[#5f4c2d]" data-testid="hero-section">
        <img src="/brand/hero.webp" alt="" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,4,2,.97)_0%,rgba(5,4,2,.82)_42%,rgba(5,4,2,.38)_72%,rgba(5,4,2,.78)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_40%,rgba(255,152,31,.13),transparent_34%)]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#070604] to-transparent" />

        <div className="relative z-10 mx-auto grid w-full max-w-[1500px] gap-12 px-5 pb-16 pt-24 md:px-10 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65 }}>
            <div className="mb-7 flex flex-wrap items-center gap-3 font-rs text-[11px] uppercase tracking-[0.16em] text-[#d7c29a]">
              <span className="border border-[#6f5b39] bg-[#171109]/90 px-3 py-2 text-[#ff981f]">World 302</span>
              <span className="flex items-center gap-2 border border-[#6f5b39] bg-[#171109]/90 px-3 py-2">
                <i className={`h-2 w-2 rounded-full ${settings.store_open === false ? "bg-[#c62828]" : "bg-[#46b96b]"}`} />
                {settings.store_open === false ? "Trading paused" : "Trading online"}
              </span>
              <span>{stats.listings || 0} names in the vault</span>
            </div>

            <div className="overline-label mb-4">Bomber RSNS / The rare name exchange</div>
            <h1 className="max-w-4xl font-display text-[clamp(4rem,8vw,8.4rem)] leading-[0.84] tracking-[-0.025em] text-[#f5ead4]" data-testid="hero-title">
              {settings.hero_title || "Own the name they remember"}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[#c7bda9] md:text-xl" data-testid="hero-subtitle">
              {settings.hero_sub || "One-of-one Old School RuneScape names, secured from the vault and removed from circulation the moment you claim them."}
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/shop" className="btn-bomb !bg-[#b72a21] !px-7 !py-4 !text-xl" data-testid="hero-shop-button">
                Open the vault <ArrowRight size={21} weight="bold" />
              </Link>
              <a href="#strike" className="btn-ghost !border-[#8b744c] !bg-[#171109]/80 !px-7 !py-4 !text-xl" data-testid="hero-strike-button">
                Place a bounty
              </a>
            </div>

            <div className="mt-9 grid max-w-2xl grid-cols-3 divide-x divide-[#5e4b2e] border-y border-[#5e4b2e] py-4">
              <div className="px-3 first:pl-0">
                <div className="font-rs text-lg font-bold text-[#ffe83a] rs-shadow">1 of 1</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#8f826e]">No restocks</div>
              </div>
              <div className="px-4">
                <div className="font-rs text-lg font-bold text-[#ffe83a] rs-shadow">Instant</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#8f826e]">Auto delist</div>
              </div>
              <div className="px-4">
                <div className="font-rs text-lg font-bold text-[#ffe83a] rs-shadow">Tracked</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#8f826e]">Order status</div>
              </div>
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.65 }}
            className="vault-frame mx-auto w-full max-w-[590px] p-3 md:p-5"
            data-testid="hero-stats"
          >
            <div className="mb-4 flex items-center justify-between border-b-2 border-black/70 px-1 pb-4">
              <div>
                <div className="font-rs text-[10px] uppercase tracking-[0.2em] text-[#a79270]">Bank of Bomber</div>
                <div className="font-display text-3xl text-[#f4ead3]">Rare name vault</div>
              </div>
              <div className="flex items-center gap-2 font-rs text-xs text-[#46b96b]">
                <span className="h-2 w-2 rounded-full bg-[#46b96b] shadow-[0_0_10px_#46b96b]" /> Live
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {slots.map((product, index) => <VaultSlot key={product?.id || index} product={product} index={index} />)}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 border-t-2 border-black/60 pt-4">
              <MarketStat label="Names live" value={stats.listings} />
              <MarketStat label="Trades done" value={stats.delivered} />
              <MarketStat label="Offer only" value={stats.offer_only} />
            </div>
            {isLoading && <div className="rs-load mt-4"><i className="w-2/3" /><span>Loading - please wait.</span></div>}
          </motion.aside>
        </div>
      </section>

      <RuneTicker items={market} />

      <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28" data-testid="categories-section">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="overline-label mb-4">Grand Exchange directory</div>
            <h2 className="font-display text-5xl leading-[0.92] text-[#f4ead3] md:text-7xl">Choose your <span className="text-[#ff981f]">market</span></h2>
          </div>
          <div className="rs-chat max-w-md !text-lg">
            <p><b>Game:</b> All listings shown below are live.</p>
            <p className="!text-[#007a00]">Buy one and it disappears from the Exchange.</p>
          </div>
        </div>
        <MarketBoard categories={categories} />
      </section>

      {featured.length > 0 && (
        <section className="border-y border-[#493b26] bg-[#0c0906]" data-testid="featured-section">
          <div className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28">
            <div className="mb-12 flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div>
                <div className="overline-label mb-4">Today&apos;s rare drops</div>
                <h2 className="font-display text-5xl leading-[0.92] text-[#f4ead3] md:text-7xl">Crown <span className="text-[#ff981f]">jewels</span></h2>
              </div>
              <Link to="/shop" className="font-rs text-sm uppercase tracking-[0.16em] text-[#ff981f] hover:text-[#ffe83a]">
                View all {stats.listings || ""} listings <ArrowRight className="ml-1 inline" />
              </Link>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featured.slice(0, 8).map((product, index) => <ProductCard key={product.id} p={product} index={index} />)}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28" data-testid="how-section">
        <div className="grid gap-12 lg:grid-cols-[.72fr_1.28fr] lg:items-start">
          <div className="lg:sticky lg:top-28">
            <div className="overline-label mb-4">Quest guide / 3 steps</div>
            <h2 className="font-display text-5xl leading-[0.92] text-[#f4ead3] md:text-7xl">A safer way to <span className="text-[#ff981f]">trade</span></h2>
            <p className="mt-6 max-w-lg text-[#a99f8c]">No forum chains. No mystery stock. Your order creates a ticket, reserves the item, and keeps the entire trade visible.</p>
          </div>
          <div className="space-y-3">
            {QUEST_STEPS.map(({ icon: Icon, label, title, copy }, index) => (
              <motion.div key={title} initial={{ opacity: 0, x: 22 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="quest-row grid gap-5 p-6 md:grid-cols-[70px_1fr_auto] md:items-center md:p-8">
                <span className="grid h-14 w-14 place-items-center border border-[#6c5939] bg-black/25 text-[#ff981f]"><Icon size={30} weight="duotone" /></span>
                <div>
                  <div className="font-rs text-[10px] uppercase tracking-[0.2em] text-[#9f8d6d]">{label}</div>
                  <div className="mt-1 font-display text-3xl text-[#f4ead3]">{title}</div>
                  <p className="mt-2 max-w-2xl text-sm text-[#a99f8c]">{copy}</p>
                </div>
                <span className="hidden font-rs text-4xl font-bold text-[#51442f] md:block">0{index + 1}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div id="strike">
        <NameStrike />
      </div>

      <NameWall items={market.slice(0, 48)} />

      <RsnChecker />

      <VouchWall />

      <section className="mx-auto max-w-[1500px] px-5 py-20 md:px-10 md:py-28">
        <div className="relative overflow-hidden border-2 border-[#6b5737] bg-[#100c07] p-8 text-center shadow-[inset_0_0_0_3px_#21180d] md:p-20">
          <img src="/brand/art_ruins.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent,rgba(7,6,4,.9)_75%)]" />
          <div className="relative mx-auto max-w-4xl">
            <div className="mb-5 inline-flex items-center gap-2 border border-[#7e6844] bg-[#21180d] px-4 py-2 font-rs text-sm font-bold text-[#ffe83a] rs-shadow" data-testid="quest-complete-badge">
              <Fire size={18} weight="fill" className="text-[#ff981f]" /> New quest available
            </div>
            <h2 className="font-display text-6xl leading-[0.88] text-[#f4ead3] md:text-9xl">Find the one they won&apos;t forget.</h2>
            <p className="mx-auto mt-6 max-w-xl text-[#b8ad99]">The vault is live. Once another player claims a name, it is gone from the market.</p>
            <Link to="/shop" className="btn-bomb mt-9 !bg-[#b72a21] !px-8 !py-4 !text-xl" data-testid="cta-shop-button">
              Browse live names <ArrowRight size={22} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <div className="hazard relative overflow-hidden border-y-2 border-black py-3" data-testid="hazard-strip">
        <div className="flex items-center justify-center gap-4 font-rs text-sm font-bold uppercase tracking-[0.16em] text-black">
          <Warning size={19} weight="fill" /> Wilderness rule: rare names do not respawn <Warning size={19} weight="fill" />
        </div>
      </div>
    </div>
  );
}
